'use client'

import Link from 'next/link'
import {
  ArrowRight,
  MachineLearningModel as BrainCircuit,
  Cloud,
  Layers,
} from '@carbon/icons-react'

import { cn } from '@/lib/utils'
import {
  ProductThumb,
  type ProductThumbVariant,
} from '@/components/product-thumb'

/**
 * The suite card, extracted so the /products and /solutions listing grids
 * share one implementation with the "Explore Our Product Suite" carousel's
 * card anatomy: full-bleed artwork band on top (no inner border or padding),
 * grain pass, padded body below, tag row pinned to the card's bottom so it
 * never drifts with description length.
 *
 * Two artwork modes:
 *  - `thumb`: the ProductThumb monogram/icon composition (products)
 *  - `art`:   a gradient artwork used directly with the entity's icon drawn
 *             straight on it (solutions that aren't single products)
 */
/* Icons resolve from string keys here (not component references) so server
   pages can describe a card's art without passing functions across the
   client boundary — that's a Next.js serialization error. */
const ART_ICONS = {
  layers: Layers,
  cloud: Cloud,
  'brain-circuit': BrainCircuit,
} as const

export interface SuiteCardArt {
  /** One of the shared gradient artworks, e.g. /gradients/01.webp */
  image: string
  icon: keyof typeof ART_ICONS
  /** Icon ink — deep on pastel zones, light on saturated ones. */
  ink: string
}

export function SuiteCard({
  href,
  title,
  description,
  tags,
  cta = 'Learn more',
  thumb,
  art,
  className,
}: {
  href: string
  title: string
  description: string
  tags?: string[]
  cta?: string
  thumb?: ProductThumbVariant
  art?: SuiteCardArt
  className?: string
}) {
  return (
    <Link
      href={href}
      className={cn(
        'group relative flex h-full flex-col overflow-hidden rounded-xl',
        'border border-border/70 bg-background',
        className
      )}
    >
      {/* Artwork band — full-bleed to the card edges */}
      <div className="relative h-[180px] md:h-[210px] w-full overflow-hidden">
        {thumb ? (
          <ProductThumb variant={thumb} />
        ) : art ? (
          (() => {
            const ArtIcon = ART_ICONS[art.icon]
            return (
              <>
                <img
                  src={art.image}
                  alt=""
                  draggable={false}
                  className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
                />
                <ArtIcon
                  aria-hidden
                  className="absolute left-1/2 top-1/2 size-24 -translate-x-1/2 -translate-y-1/2"
                  style={{ color: art.ink }}
                />
                {/* Dark theme mutes the pastel art, same move as the deck */}
                <div className="pointer-events-none absolute inset-0 hidden bg-black/20 dark:block" />
              </>
            )
          })()
        ) : null}
        {/* Grain pass — one texture across every artwork on the site */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: 'url(/gradients/grain.webp)',
            backgroundRepeat: 'repeat',
            backgroundSize: '512px 512px',
            mixBlendMode: 'overlay',
            opacity: 0.4,
          }}
        />
      </div>

      {/* Body — padding on the text only */}
      <div className="flex flex-1 flex-col p-5 md:p-6">
        {/* h2, not h3: both grids that use this card (products, solutions) sit
            directly under the page h1 with no section heading between, so an
            h3 here skipped a level in the document outline. */}
        <h2 className="font-heading mb-2 text-[18px] font-normal leading-[1.25] text-foreground md:mb-3 md:text-[22px]">
          {title}
        </h2>
        <p className="text-[14px] leading-[1.7] text-muted-foreground md:text-[15px]">
          {description}
        </p>
        {/* Pinned to the bottom so tags + CTA sit on one line across cards */}
        {tags && tags.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1.5 pt-4 md:gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-[4px] bg-emerald-600/10 px-2 py-0.5 text-[10px] font-medium text-foreground backdrop-blur-md md:px-2.5 md:py-1 md:text-[11px]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <span
          className={cn(
            'inline-flex w-fit items-center gap-1.5 pt-6 text-[14px] font-medium text-foreground md:text-[15px]',
            (!tags || tags.length === 0) && 'mt-auto'
          )}
        >
          {cta}
          <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  )
}

export default SuiteCard
